import * as React from "react";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type {
  IContextualMenuProps,
  ITheme,
  BaseButton,
  Button,
} from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { memoizeFunction, nullRender } from "@fluentui/react";

import {
  dataUniqueId,
  getPreventMenuClickCallback,
  isIdleCallbackEnabled,
  UILocation,
  uniqueDropdownId,
} from "@1js/acui-common";
import type { AppContextType } from "@1js/acui-context";
import { AppContext } from "@1js/acui-context";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";
import { getDownChevronIcon, iconNameToIconProps } from "@1js/acui-icons";
import { defaultKeytipExecute } from "@1js/acui-keytip";
import type {
  ControlInMenuRendererFunction,
  MenuDefinition,
} from "@1js/acui-menu";
import {
  AppContextualMenu,
  convertMenuDefinitionToFabricMenuProps,
  DefaultMenuAnchorContext,
  emptyMenuProps,
  MenuAnchorContext,
} from "@1js/acui-menu";
import {
  getShadowRenderingContextValue,
  getShadowRenderMenuValue,
  ShadowRenderingContext,
} from "@1js/acui-shadow-rendering";
import { AppThemeConsumer } from "@1js/acui-theme";
import { AppTooltipHost, getTooltipText } from "@1js/acui-tooltip";

import { setTooltipsOnMenuItems } from "../../utilities";

import { deriveButtonKeytip } from "@1js/acui-keytip";
import { AppBaseButton } from "../AppBaseButton";
import type { AppFlyoutAnchorInternalProps } from "./AppFlyoutAnchor.Props";
import { getAppFlyoutAnchorStyles } from "./AppFlyoutAnchor.styles";

interface AppFlyoutAnchorState {
  // Whether the menu has been opened at least once after mounting
  menuHasBeenOpened?: boolean;
  // Whether the menu is in the shadow rendering phase.
  isShadowRendering?: boolean;
}

export class AppFlyoutAnchor extends React.PureComponent<
  AppFlyoutAnchorInternalProps,
  AppFlyoutAnchorState
> {
  private isMenuOpen = false;
  private baseButton = React.createRef<AppBaseButton>();
  private tooltipHost: AppTooltipHost | undefined;
  private tooltipHostMap: Map<string, AppTooltipHost> = new Map();
  private initiatedShadowRendering = false;
  // We will default to a generic ID and set a specific Id with the
  // custom menu Id when we render
  private flyoutDropdownId: string = "FlyoutDropDownId";
  private menuExecutionEvent: Event | undefined;

  static defaultProps = {
    directionalHintFixed: true,
    useTargetAsMinWidth: true,
  };

  static contextType: React.Context<AppContextType> = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  constructor(props: AppFlyoutAnchorInternalProps) {
    super(props);

    this.state = {
      menuHasBeenOpened: false,
      isShadowRendering: false,
    };

    // Set a unique ID for the dropdown
    this.flyoutDropdownId = uniqueDropdownId(this.props, this.props.id);
  }

  public render() {
    const {
      id,
      hideLabel,
      hideChevron,
      hideIcon,
      label,
      icon,
      disabled,
      iconColor,
      onRenderText,
      onRenderIcon,
      lang,
      role,
      toggled,
      ariaLabel,
      labelInMenu,
      shadowRenderMenu,
      deferredMenuDefinition,
      controlInMenuRendererFunction,
      menuDefinition,
      preventMenuExecution,
      noDomId,
      delayUpdateFocusOnHover,
    } = this.props;

    return (
      <ShadowRenderingContext.Provider
        value={getShadowRenderingContextValue(!!this.state.isShadowRendering)}
      >
        <AppThemeConsumer>
          {(theme: ITheme) => (
            <MenuAnchorContext.Provider
              value={
                deferredMenuDefinition
                  ? {
                      deferredMenuDefinition,
                      getMenuPropsFromMenuDefinition:
                        this.getMenuPropsFromMenuDefinitionFunction(
                          controlInMenuRendererFunction,
                          theme
                        ),
                    }
                  : DefaultMenuAnchorContext
              }
            >
              <AppBaseButton
                menuAs={deferredMenuDefinition?.menuAs ?? AppContextualMenu}
                id={noDomId ? undefined : id}
                ref={this.baseButton}
                menuProps={{
                  ...this.getMenuPropsFromMenuDefinitionFunction(
                    controlInMenuRendererFunction,
                    theme
                  )(menuDefinition),
                  delayUpdateFocusOnHover,
                }}
                onFocus={this.onFocus}
                onBlur={this.onBlur}
                styles={getAppFlyoutAnchorStyles(
                  theme,
                  !!hideChevron,
                  this.props.styles
                )}
                disabled={disabled}
                onRenderMenuIcon={hideChevron ? nullRender : undefined}
                iconProps={iconNameToIconProps(icon, iconColor, hideIcon)}
                menuIconProps={iconNameToIconProps(
                  getDownChevronIcon(this.props)
                )}
                onAfterMenuDismiss={this.onAfterMenuDismiss}
                text={label && !hideLabel ? label : undefined}
                data-automation-type={
                  this.props.dataAutomationType || "AppFlyoutAnchor"
                }
                data-unique-id={dataUniqueId(this.props)}
                onMouseDown={this.props.onMouseDown}
                onMouseUp={this.props.onMouseUp}
                onPointerEnter={this.props.onPointerEnter}
                title={this.props.title}
                keytipProps={deriveButtonKeytip(
                  this.props.keytipProps,
                  this.keytipExecute
                )}
                ariaLabel={ariaLabel || labelInMenu || label}
                ariaDescription={this.props.customTooltip}
                onRenderText={onRenderText}
                onRenderIcon={onRenderIcon}
                role={role}
                lang={lang}
                checked={toggled}
                toggle={toggled !== undefined}
                persistMenu={this.shouldPersistMenu()}
                renderPersistedMenuHiddenOnMount={getShadowRenderMenuValue(
                  shadowRenderMenu
                )} // This helps shadow rendering to incrementally mount menu items when hidden.
                data-nav={this.props.dataNav}
                menuTriggerKeyCode={this.props.menuTriggerKeyCode}
                onMenuClick={
                  preventMenuExecution
                    ? getPreventMenuClickCallback(this.executeMenuButton)
                    : this.setMenuExecutionEvent
                }
                data-is-focusable={this.props.dataIsFocusable}
                aria-keyshortcuts={this.props.keyShortcuts}
              />
            </MenuAnchorContext.Provider>
          )}
        </AppThemeConsumer>
      </ShadowRenderingContext.Provider>
    );
  }

  public componentDidMount() {
    if (this.context.setTooltipElement && this.context.updateTooltipsHidden) {
      this.tooltipHost = new AppTooltipHost({
        updateTooltipsHidden: this.context.updateTooltipsHidden,
        setTooltipElement: this.context.setTooltipElement,
        htmlElement: this.baseButton.current?.buttonElement,
        controlId: this.props.id,
        controlLabel: getTooltipText(
          this.props.hideLabel,
          this.props.label,
          this.props.customTooltip
        ),
        tooltipTopMargin: this.props.fancyTooltipTopMargin,
        tooltipDirection: this.props.tooltipDirection,
        performSetOnNextFrame: true,
        targetOverflowSetItem: !!checkExperiment(
          this.context,
          ribbonExperiments.tooltipsTargetOverflowSetItemEnabled
        ),
        location: this.props.location,
      });
      this.tooltipHost.registerTooltipHostActions();
    }

    this.tryInitiateShadowRendering();
  }

  public componentDidUpdate(prevProps: AppFlyoutAnchorInternalProps) {
    this.tryInitiateShadowRendering();
    if (
      this.tooltipHost &&
      (prevProps.customTooltip !== this.props.customTooltip ||
        prevProps.label !== this.props.label ||
        prevProps.hideLabel !== this.props.hideLabel)
    ) {
      this.tooltipHost.updateTooltip(
        getTooltipText(
          this.props.hideLabel,
          this.props.label,
          this.props.customTooltip
        )
      );
    }
    // Ensure we never pass along a stale event
    this.menuExecutionEvent = undefined;
  }

  public componentWillUnmount() {
    if (this.tooltipHost) {
      this.tooltipHost.unregisterTooltipHostActions();
      this.tooltipHost = undefined;
    }

    // If we are unmounting but the menu is open, dismiss the menu
    if (this.isMenuOpen) {
      this.dismissMenu();
      this.onAfterMenuDismiss();
    }
  }

  public openMenu = (
    shouldFocusOnContainer?: boolean,
    shouldFocusOnMount?: boolean
  ) => {
    if (this.baseButton.current) {
      this.baseButton.current.openMenu(
        shouldFocusOnContainer,
        shouldFocusOnMount
      );

      this.isMenuOpen = true;
    }
  };

  public dismissMenu = () => {
    if (this.baseButton.current) {
      this.baseButton.current.dismissMenu();
      this.isMenuOpen = false;
    }
  };

  public focus = () => {
    this.baseButton.current && this.baseButton.current.focus();
  };

  // Method outputs a function to generate full menu props from menuDefinition. In case of deferred menus, the output can be passed into the menu
  // so that it can generate full menu props using menu definition it fetches from the store.
  // The outer function should take in all parameters other than menuDefinition that can change the generation of the menuProps.
  private getMenuPropsFromMenuDefinitionFunction = memoizeFunction(
    (
      controlInMenuRendererFunction: ControlInMenuRendererFunction,
      theme: ITheme
    ) =>
      memoizeFunction(
        (menuDefinition: MenuDefinition | undefined): IContextualMenuProps => {
          const {
            label,
            location,
            directionalHint,
            directionalHintFixed,
            useTargetAsMinWidth,
            calloutWidth,
            menuHostId,
            shouldFocusOnContainer,
            shouldFocusOnMount,
          } = this.props;

          const baseMenuProps = menuDefinition
            ? convertMenuDefinitionToFabricMenuProps(
                {
                  menuDefinition,
                  controlInMenuRendererFunction,
                  location,
                  onSubMenuPositioned: this.onSubMenuReady,
                  customMenuItemStyles: this.props.menuItemStyles,
                  customMenuClassNames: this.props.menuClassNames,
                  arePerfImprovementsEnabled:
                    this.context.isExperimentalFeatureEnabled?.(
                      "ContextualUIPerfImprovementsIsEnabled"
                    ),
                  onMenuPositioned: this.onCalloutReady,
                  theme,
                  terminalParentControlId: this.getTerminalParentControlId(),
                  useFullDescriptionLabelOnly:
                    this.context.isExperimentalFeatureEnabled?.(
                      ribbonExperiments.useFullDescriptionLabelOnly
                    ),
                  isExperimentalFeatureEnabled:
                    this.context.isExperimentalFeatureEnabled,
                },
                directionalHintFixed,
                this.context.appWindow,
                this.context.responsiveClassName
              )
            : emptyMenuProps;

          return {
            ...baseMenuProps,
            onMenuOpened: this.onMenuOpened,
            directionalHint,
            useTargetAsMinWidth,
            ariaLabel: label,
            id: this.flyoutDropdownId,
            shouldFocusOnContainer,
            shouldFocusOnMount,
            calloutProps: {
              ...baseMenuProps.calloutProps,
              layerProps: { hostId: menuHostId },
              calloutWidth,
              preventDismissOnScroll: this.props.preventCalloutDismissOnScroll,
              shouldDismissOnWindowFocus: this.props.shouldDismissOnWindowFocus,
            },
            focusZoneProps: {
              shouldInputLoseFocusOnArrowKey: () =>
                this.props.enableDownArrowNavigation ?? false,
            },
          };
        }
      )
  );

  private keytipExecute = (executeTarget: HTMLElement) => {
    const { shouldFocusOnContainer, shouldFocusOnMount } = this.props;
    defaultKeytipExecute(executeTarget, () =>
      this.openMenu(shouldFocusOnContainer, shouldFocusOnMount)
    );
  };

  private onAfterMenuDismiss = () => {
    if (this.props.onAfterMenuDismiss) {
      this.props.onAfterMenuDismiss(this.props.id);
    }
    if (this.tooltipHost) {
      this.tooltipHost.hideAllTooltips(false);
    }
    if (this.tooltipHostMap) {
      this.unregisterTooltips();
    }
  };

  private unregisterTooltips() {
    this.tooltipHostMap.forEach((tooltipHost) => {
      tooltipHost.unregisterTooltipHostActions();
    });
    this.tooltipHostMap.clear();
  }

  private onMenuOpened = () => {
    this.isMenuOpen = true;

    if (!this.state.menuHasBeenOpened) {
      this.setState({
        menuHasBeenOpened: true,
      });
    }

    this.resetShadowRenderingAfterUserAction();
    this.executeMenuButton();

    if (this.tooltipHost) {
      this.tooltipHost.hideAllTooltips(true);
    }
  };

  private executeMenuButton = () => {
    const { id, location } = this.props;
    if (this.props.onExecute) {
      this.props.onExecute({
        id,
        location,
        inputMethod: this.baseButton?.current?.getInputMethod(),
        event: this.menuExecutionEvent,
      });
      this.menuExecutionEvent = undefined;
    }
  };

  private onFocus = () => {
    if (this.props.onFocus) {
      this.props.onFocus(this.props.id);
    }
  };

  private onBlur = () => {
    if (this.props.onBlur) {
      this.props.onBlur(this.props.id);
    }
  };

  private onSubMenuReady = (
    menuDefinition: MenuDefinition | undefined,
    flyoutMenuId?: string
  ) => {
    if (flyoutMenuId) {
      this.tooltipHostMap = setTooltipsOnMenuItems(
        menuDefinition,
        flyoutMenuId + "MenuId",
        this.tooltipHostMap,
        this.context.setTooltipElement,
        this.context.updateTooltipsHidden,
        true /** perform on next frame */,
        this.context.appWindow
      );
    }
  };

  private onCalloutReady = (menuDefinition: MenuDefinition | undefined) => {
    if (this.tooltipHostMap.size === 0) {
      this.tooltipHostMap = setTooltipsOnMenuItems(
        menuDefinition,
        this.flyoutDropdownId,
        this.tooltipHostMap,
        this.context.setTooltipElement,
        this.context.updateTooltipsHidden,
        true /** perform on next frame */,
        this.context.appWindow
      );
    }
  };

  private shouldPersistMenu() {
    // this should only return true when menu has been opened at least once.
    return this.props.persistMenu && this.state.menuHasBeenOpened;
  }

  /**
   * This method is called on componentDidMount and componentDidUpdate.
   * If shadow rendering has already been initiated this is no-op.
   * If not, it tries to initiate it.
   * The reason why we have to call this multiple times on update is because
   * we do not want to initiate shadow rendering when the control is disabled.
   */
  private tryInitiateShadowRendering = () => {
    if (
      !this.initiatedShadowRendering &&
      getShadowRenderMenuValue(this.props.shadowRenderMenu) &&
      isIdleCallbackEnabled() &&
      !this.props.disabled &&
      this.state.menuHasBeenOpened === false
    ) {
      this.initiatedShadowRendering = true;
      window.requestIdleCallback(
        () => {
          const { id, location } = this.props;
          if (this.props.onExecute) {
            this.props.onExecute({ id, location, isShadowRendering: true });
          }
        },
        undefined /*options*/,
        // @ts-ignore Bug:5821196 - [TS 4.5.2 upgrade OOUI] Fix requestIdleCallback across OOUI
        `Flyout-${this.props.id}-ShadowMenuOpen`
      );

      window.requestIdleCallback(
        () => {
          if (this.state.menuHasBeenOpened === false) {
            this.setState({
              isShadowRendering: true,
              menuHasBeenOpened: true,
            });
          }
        },
        undefined /*options*/,
        // @ts-ignore Bug:5821196 - [TS 4.5.2 upgrade OOUI] Fix requestIdleCallback across OOUI
        `Flyout-${this.props.id}-StartShadowRendering`
      );
    }
  };

  /**
   * If a user opens the menu by clicking, keyboarding, through keytips
   * or through the API, the first thing we need to do is set shadow rendering
   * to false.
   */
  private resetShadowRenderingAfterUserAction = () => {
    if (this.state.isShadowRendering) {
      this.setState({
        isShadowRendering: false,
      });
    }
  };

  private setMenuExecutionEvent = (
    ev?:
      | React.MouseEvent<HTMLElement | BaseButton | Button>
      | React.KeyboardEvent<HTMLElement | BaseButton | Button>
  ) => {
    this.menuExecutionEvent = ev?.nativeEvent;
  };

  // Returns the control ID of the top-most parent flyout that isn't an overflow. Return logic must be 1-1 mapping with the function in useGetMenuProps.
  // Don't set terminal parentControlId with the top-most control if that control would be an overflow.
  private getTerminalParentControlId = () => {
    const { id, location, terminalParentControlId } = this.props;
    return location !== UILocation.Overflow
      ? (terminalParentControlId ?? id)
      : terminalParentControlId;
  };
}
